import { ImageLightbox } from '@/components/ui/image-lightbox';
import { motion } from 'framer-motion';
import React, { useCallback, useMemo, useState } from 'react';

const galleryImages = [
  '/images/gallery/selected/001.webp',
  '/images/gallery/selected/002.webp',
  '/images/gallery/selected/003.webp',
  '/images/gallery/selected/004.webp',
  '/images/gallery/selected/005.webp',
  '/images/gallery/selected/006.webp',
  '/images/gallery/selected/007.webp',
  '/images/gallery/selected/008.webp',
  '/images/gallery/selected/009.webp',
  '/images/gallery/selected/010.webp',
  '/images/gallery/selected/011.webp',
  '/images/gallery/selected/012.webp',
  '/images/gallery/selected/013.webp',

]

interface ImageData {
  image: string;
  size: 'small' | 'medium' | 'large';
  index: number;
}

const GalleryItem = React.memo(function GalleryItem({
  imageData,
  onSelect
}: {
  imageData: ImageData;
  onSelect: (index: number) => void;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.5,
        delay: imageData.index * 0.02,
        type: "spring",
        stiffness: 260,
        damping: 20
      }}
      className="cursor-pointer"
      style={{
        gridColumn: imageData.index === 5 ? '2 / span 2' : 'auto',
        gridRow: imageData.index === 5 ? '2 / span 2' : 'auto',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect(imageData.index)}
    >
      <motion.div
        whileHover={{ scale: 1.05 }}
        className={`relative w-full h-full overflow-hidden shadow-lg bg-white ${
          imageData.size === 'large'
            ? 'rounded-[30px]'
            : imageData.size === 'medium'
            ? 'rounded-[25px]'
            : 'rounded-[20px]'
        }`}
        style={{
          boxShadow: isHovered
            ? '0 20px 40px rgba(0,0,0,0.2)'
            : '0 4px 20px rgba(0,0,0,0.1)',
          transition: 'box-shadow 0.3s ease',
        }}
      >
        <img
          src={imageData.image}
          alt={`Gallery ${imageData.index + 1}`}
          className="w-full h-full object-cover"
          loading="lazy"
          draggable={false}
        />
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-pastel-pink/10 to-pastel-purple/10"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />
      </motion.div>
    </motion.div>
  );
});

export default function Gallery() {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const [isOpen, setIsOpen] = useState(false);

  const imageLayout = useMemo(() => {
    const layout: ImageData[] = [];

    // 画像のサイズを定義
    // index 5が中央の大きな画像（2x2）
    // 周囲の中サイズ、その他は小サイズ
    const getSize = (index: number): 'small' | 'medium' | 'large' => {
      if (index === 5) return 'large';  // 中央
      if ([1, 3, 9, 11].includes(index)) return 'medium';  // 中央の上下左右
      return 'small';
    };

    galleryImages.forEach((image, index) => {
      layout.push({
        image,
        size: getSize(index),
        index
      });
    });

    return layout;
  }, []);


  const handleImageSelect = useCallback((index: number) => {
    setSelectedImageIndex(index);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setSelectedImageIndex(null), 200);
  }, []);

  return (
    <div className="fairy-card">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-3xl md:text-4xl font-dancing text-center text-gray-800 Gallery mb-8"
      >
        Gallery
      </motion.h2>

      <div>
        <div
          className="grid gap-3 md:gap-4"
          style={{
            gridTemplateColumns: 'repeat(4, 1fr)',
            gridAutoRows: 'minmax(120px, 1fr)',
          }}
        >
          {imageLayout.map((imageData) => (
            <GalleryItem
              key={imageData.index}
              imageData={imageData}
              onSelect={handleImageSelect}
            />
          ))}
        </div>
      </div>

      {/* フルスクリーンライトボックス */}
      <ImageLightbox
        images={galleryImages}
        selectedIndex={selectedImageIndex}
        isOpen={isOpen}
        onClose={handleClose}
        onIndexChange={setSelectedImageIndex}
        slideDirection={slideDirection}
        onSlideDirectionChange={setSlideDirection}
      />
    </div>
  )
}
